import React, { useMemo } from 'react';
import { View, Text, StyleSheet, Pressable } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../styles/ThemeProvider';
import { useThemePreference, type ThemePreference } from '../styles/ThemeProvider';

interface Option {
  value: ThemePreference;
  label: string;
  icon: keyof typeof Ionicons.glyphMap;
}

const OPTIONS: readonly Option[] = [
  { value: 'light', label: 'Light', icon: 'sunny-outline' },
  { value: 'dark', label: 'Dark', icon: 'moon-outline' },
  { value: 'system', label: 'System', icon: 'phone-portrait-outline' },
] as const;

export default function AppearanceToggle() {
  const theme = useTheme();
  const { preference, setPreference } = useThemePreference();

  // The accent (teal) is mid-tone in both palettes, so the active option
  // needs near-white content on top of it for legible contrast. In dark
  // mode `backgroundPrimary` is near-black — using it here would render
  // dark-on-teal. Pick whichever palette token is light in each mode:
  // dark uses `textPrimary` (warm off-white), light uses `backgroundPrimary`
  // (warm off-white).
  const onAccentColor =
    theme.mode === 'dark' ? theme.colors.textPrimary : theme.colors.backgroundPrimary;

  const styles = useMemo(
    () =>
      StyleSheet.create({
        wrapper: {
          marginTop: theme.spacing.lg,
        },
        description: {
          ...theme.typography.sm,
          color: theme.colors.textSecondary,
          marginBottom: theme.spacing.md,
        },
        segmented: {
          flexDirection: 'row',
          backgroundColor: theme.colors.backgroundElevated,
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
          borderRadius: theme.radius.radiusMd,
          padding: theme.spacing.xxs,
        },
        option: {
          flex: 1,
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: theme.spacing.xs,
          paddingVertical: theme.spacing.sm,
          paddingHorizontal: theme.spacing.sm,
          borderRadius: theme.radius.radiusSm,
        },
        optionActive: {
          backgroundColor: theme.colors.accent,
        },
        optionLabel: {
          ...theme.typography.sm,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.textSecondary,
        },
        optionLabelActive: {
          color: onAccentColor,
        },
      }),
    [theme, onAccentColor],
  );

  return (
    <View style={styles.wrapper}>
      <Text style={styles.description}>
        Light, dark, or follow your system setting.
      </Text>
      <View style={styles.segmented} accessibilityRole="radiogroup">
        {OPTIONS.map((option) => {
          const active = preference === option.value;
          return (
            <Pressable
              key={option.value}
              onPress={() => setPreference(option.value)}
              style={[styles.option, active && styles.optionActive]}
              accessibilityRole="radio"
              accessibilityState={{ selected: active }}
              accessibilityLabel={option.label}
            >
              <Ionicons
                name={option.icon}
                size={16}
                color={active ? onAccentColor : theme.colors.textSecondary}
              />
              <Text style={[styles.optionLabel, active && styles.optionLabelActive]}>
                {option.label}
              </Text>
            </Pressable>
          );
        })}
      </View>
    </View>
  );
}
